import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle, ChevronLeft, ChevronRight, MapPin, PackageCheck, Search, ShoppingCart, X } from "lucide-react";
import { toast } from "sonner";
import MacJitLogo from "../components/MacJitLogo";
import api from "../lib/api";
import { isValidIndianPhone, normalizeIndianPhone } from "../lib/validation";

const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;
const PAGE_SIZE = 9;

export default function PartsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [city, setCity] = useState(() => localStorage.getItem("macjit_parts_city") || "");
  const [pincode, setPincode] = useState(() => localStorage.getItem("macjit_parts_pincode") || "");
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (city.trim()) params.set("city", city.trim());
    if (pincode.trim()) params.set("pincode", pincode.trim());
    api.get(`/public/inventory${params.toString() ? `?${params}` : ""}`)
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Unable to load parts right now"))
      .finally(() => setLoading(false));
  }, [city, pincode]);

  useEffect(() => {
    if (city.trim()) localStorage.setItem("macjit_parts_city", city.trim());
    else localStorage.removeItem("macjit_parts_city");
    if (pincode.trim()) localStorage.setItem("macjit_parts_pincode", pincode.trim());
    else localStorage.removeItem("macjit_parts_pincode");
  }, [city, pincode]);

  const categories = useMemo(() => {
    const values = items.map((i) => i.category || "Stock").filter(Boolean);
    return ["all", ...Array.from(new Set(values)).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      const cat = item.category || "Stock";
      const categoryOk = category === "all" || cat === category;
      const queryOk = !q || `${item.name || ""} ${item.sku || ""} ${item.category || ""} ${item.description || ""}`.toLowerCase().includes(q);
      return categoryOk && queryOk;
    });
  }, [items, query, category]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const rangeStart = filtered.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filtered.length);

  const clearLocation = () => {
    setCity("");
    setPincode("");
  };

  useEffect(() => { setPage(1); }, [query, category, city, pincode]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="sticky top-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button onClick={() => nav("/")} className="flex items-center gap-3 text-left">
            <MacJitLogo size={34} />
            <div className="leading-none">
              <span className="font-display font-black text-xl tracking-tighter block"><span className="text-white">MAC</span><span className="text-orange-500">JIT</span></span>
              <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-orange-500/80">Browse Parts</span>
            </div>
          </button>
          <button onClick={() => nav("/")} className="border border-zinc-800 hover:border-orange-500 text-zinc-300 hover:text-orange-500 px-4 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center gap-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <section className="grid lg:grid-cols-12 gap-8 mb-10">
          <div className="lg:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">Live inventory</p>
            <h1 className="font-display font-black text-5xl lg:text-7xl uppercase tracking-tighter leading-[0.95]">
              Book parts<br /><span className="text-orange-500">before they sell.</span>
            </h1>
          </div>
          <p className="lg:col-span-5 text-zinc-400 font-mono text-sm self-end leading-relaxed">
            These parts come from workshop inventory. Send a pickup request and our team will connect soon; stock is locked only after confirmation.
          </p>
        </section>

        <div className="border border-zinc-800 bg-zinc-900/50 p-4 mb-8 grid md:grid-cols-[1fr_auto] gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by product, SKU, category, details..."
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-orange-500 outline-none pl-10 pr-10 py-3 font-mono text-sm"
            />
            {query && <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>}
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="bg-zinc-950 border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 font-mono text-sm min-w-48">
            {categories.map((c) => <option key={c} value={c}>{c === "all" ? "All categories" : c}</option>)}
          </select>
        </div>

        <div className="border border-zinc-800 bg-black/60 p-4 mb-8 grid gap-3 lg:grid-cols-[1fr_170px_170px_auto] lg:items-end">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-orange-500 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> Seller location
            </p>
            <p className="mt-1 text-sm text-zinc-500">Filter by seller city or pincode. Clear it anytime to browse all cities.</p>
          </div>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">City</span>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Bengaluru"
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-orange-500 outline-none px-3 py-3 font-mono text-sm"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Pincode</span>
            <input
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="560066"
              inputMode="numeric"
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-orange-500 outline-none px-3 py-3 font-mono text-sm"
            />
          </label>
          <button onClick={clearLocation} disabled={!city && !pincode} className="border border-zinc-700 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500 disabled:opacity-40">
            All cities
          </button>
        </div>

        {loading ? (
          <div className="border border-zinc-800 bg-zinc-900/40 p-10 text-center font-mono text-xs text-zinc-500">Loading available parts...</div>
        ) : filtered.length === 0 ? (
          <div className="border border-zinc-800 bg-zinc-900/40 p-10 text-center">
            <PackageCheck className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
            <p className="font-display font-black text-2xl">No matching stock found</p>
            <p className="text-zinc-500 text-sm mt-1">Try a different search or category.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 border border-zinc-800 bg-zinc-900/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                Showing {rangeStart}-{rangeEnd} of {filtered.length} parts{city || pincode ? ` near ${[city, pincode].filter(Boolean).join(" ")}` : " across all sellers"}
              </p>
              <PaginationControls page={currentPage} totalPages={totalPages} onPage={setPage} />
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
              {pagedItems.map((item) => <PartCard key={item.id} item={item} onBook={() => setSelected(item)} />)}
            </div>
            <PaginationControls page={currentPage} totalPages={totalPages} onPage={setPage} alignEnd />
          </div>
        )}
      </main>

      {selected && <BookingPanel item={selected} onClose={() => setSelected(null)} city={city} pincode={pincode} />}
    </div>
  );
}

function PaginationControls({ page, totalPages, onPage, alignEnd = false }) {
  if (totalPages <= 1) return null;
  const pages = Array.from({ length: totalPages }, (_, index) => index + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1);
  const visible = pages.reduce((acc, p, index) => {
    if (index > 0 && p - pages[index - 1] > 1) acc.push("gap-" + p);
    acc.push(p);
    return acc;
  }, []);
  const buttonBase = "grid h-9 min-w-9 place-items-center border font-mono text-[10px] uppercase tracking-widest transition-colors";

  return (
    <div className={`flex flex-wrap items-center gap-2 ${alignEnd ? "justify-end" : ""}`}>
      <button
        onClick={() => onPage(Math.max(1, page - 1))}
        disabled={page <= 1}
        className={`${buttonBase} border-zinc-800 text-zinc-300 hover:border-orange-500 hover:text-orange-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-zinc-800 disabled:hover:text-zinc-300`}
        aria-label="Previous parts page"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      {visible.map((p) => typeof p === "string" ? (
        <span key={p} className="grid h-9 min-w-9 place-items-center font-mono text-xs text-zinc-600">...</span>
      ) : (
        <button
          key={p}
          onClick={() => onPage(p)}
          className={`${buttonBase} ${p === page ? "border-orange-500 bg-orange-500 text-black" : "border-zinc-800 text-zinc-300 hover:border-orange-500 hover:text-orange-500"}`}
        >
          {p}
        </button>
      ))}
      <button
        onClick={() => onPage(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className={`${buttonBase} border-zinc-800 text-zinc-300 hover:border-orange-500 hover:text-orange-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-zinc-800 disabled:hover:text-zinc-300`}
        aria-label="Next parts page"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function PartCard({ item, onBook }) {
  const stock = Number(item.stock || 0);
  const low = stock <= Number(item.low_stock_threshold || 5);
  return (
    <article className="bg-zinc-950 p-6 flex flex-col min-h-[300px] hover:bg-orange-500/5 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{item.category || "Stock"} · {item.sku || "No SKU"}</p>
          <h2 className="font-display font-black text-2xl tracking-tight mt-2">{item.name}</h2>
        </div>
        {low && (
          <span className="bg-orange-500/10 border border-orange-500/40 text-orange-500 font-mono text-[10px] uppercase tracking-widest px-2 py-1 flex items-center gap-1 shrink-0">
            <AlertTriangle className="w-3 h-3" /> Sell fast
          </span>
        )}
      </div>
      <p className="text-zinc-500 text-sm mt-4 flex-1">{item.description || item.details || "Workshop-verified inventory item available for customer booking."}</p>
      <p className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
        <MapPin className="h-3.5 w-3.5 text-orange-500" />
        {item.seller_location || item.seller_city || "Seller location"}
      </p>
      <div className="grid grid-cols-2 gap-px bg-zinc-800 my-5">
        <div className="bg-zinc-900 p-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Price</p>
          <p className="font-display font-black text-2xl text-orange-500">{money(item.price)}</p>
        </div>
        <div className="bg-zinc-900 p-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Stock</p>
          <p className="font-display font-black text-2xl">{stock}</p>
        </div>
      </div>
      <button onClick={onBook} className="bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-widest py-3 flex items-center justify-center gap-2 transition-colors">
        <ShoppingCart className="w-4 h-4" /> Book product
      </button>
    </article>
  );
}

function BookingPanel({ item, onClose, city, pincode }) {
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", qty: "1", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidIndianPhone(form.customer_phone)) return toast.error("Enter a valid Indian mobile number");
    setBusy(true);
    try {
      await api.post("/public/product-bookings", {
        inventory_id: item.id,
        customer_name: form.customer_name,
        customer_phone: normalizeIndianPhone(form.customer_phone),
        qty: Number(form.qty || 1),
        note: [
          form.note,
          city || pincode ? `Customer searched from: ${[city, pincode].filter(Boolean).join(" ")}` : "",
          item.seller_location ? `Seller location: ${item.seller_location}` : "",
        ].filter(Boolean).join("\n"),
      });
      toast.success("Request sent successfully. Our team will connect soon.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not book this product");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center px-4">
      <form onSubmit={submit} className="w-full max-w-lg bg-zinc-950 border border-zinc-800 p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Book product</p>
            <h3 className="font-display font-black text-3xl tracking-tight mt-1">{item.name}</h3>
            <p className="font-mono text-xs text-zinc-500 mt-1">{item.sku || "No SKU"} · {money(item.price)} · {item.stock} in stock</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-orange-300 mt-2">{item.seller_location || "Seller location available after confirmation"}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-zinc-900 text-zinc-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <input required value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Customer name" className="w-full bg-zinc-900 border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 font-mono text-sm" />
          <input required value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} onBlur={() => form.customer_phone && set("customer_phone", normalizeIndianPhone(form.customer_phone))} placeholder="Customer phone" className="w-full bg-zinc-900 border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 font-mono text-sm" />
          <input required min="1" max={item.stock || 1} type="number" value={form.qty} onChange={(e) => set("qty", e.target.value)} placeholder="Quantity" className="w-full bg-zinc-900 border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 font-mono text-sm" />
          <textarea value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Optional note for our team" className="w-full bg-zinc-900 border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 font-mono text-sm min-h-24" />
        </div>
        <p className="font-mono text-[10px] text-zinc-500 mt-4">Admin will confirm the customer first. Stock locks only after confirmation for a specific hold time.</p>
        <button disabled={busy} className="mt-5 w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-black font-display font-black uppercase tracking-widest py-3 transition-colors">
          {busy ? "Sending..." : "Send booking request"}
        </button>
      </form>
    </div>
  );
}
